import { authSession } from "@/features/auth";
import {
  TelemetryCollector,
  installTelemetryCollector,
  SESSION_IDLE_MS,
  type UsageSession,
} from "@/features/telemetry";
import { sendTelemetryBatch } from "@/services/telemetry/transport";
import type { TelemetryScope } from "@/services/telemetry/contracts";
import { env } from "@/app/config/env";

/** Owns the browser lifetime outside remounting React trees. Only small usage-session markers are persisted. */
export function connectTelemetryLifecycle(
  browser: Window | undefined = typeof window === "undefined"
    ? undefined
    : window,
) {
  if (!browser || !env.VITE_TELEMETRY_ENABLED) return () => {};
  const browserWindow = browser;
  const fallback = new Map<string, UsageSession>();
  const keyFor = (scope: TelemetryScope) =>
    `kayer_usage_v1:${scope.personId}:${scope.tenantId}`;
  const now = () => Date.now();
  const id = () => browser.crypto.randomUUID();
  function read(scope: TelemetryScope, time: number) {
    const key = keyFor(scope);
    let value = fallback.get(key);
    try {
      const raw: unknown = JSON.parse(
        browserWindow.localStorage.getItem(key) ?? "null",
      );
      if (
        raw &&
        typeof raw === "object" &&
        "id" in raw &&
        typeof raw.id === "string" &&
        /^[a-f0-9-]{36}$/.test(raw.id) &&
        "lastActivity" in raw &&
        typeof raw.lastActivity === "number" &&
        Number.isFinite(raw.lastActivity)
      )
        value = { id: raw.id, lastActivity: raw.lastActivity };
    } catch {
      /* In-memory markers still work if storage is unavailable. */
    }
    if (
      value &&
      time - value.lastActivity < SESSION_IDLE_MS &&
      time >= value.lastActivity
    )
      return { value, created: false };
    value = { id: id(), lastActivity: time };
    write(scope, value);
    return { value, created: true };
  }
  function write(scope: TelemetryScope, value: UsageSession) {
    fallback.set(keyFor(scope), value);
    try {
      browserWindow.localStorage.setItem(keyFor(scope), JSON.stringify(value));
    } catch {
      /* Optional session continuity cannot prevent app use. */
    }
  }
  const navigation = browser.performance.getEntriesByType("navigation")[0] as
    PerformanceNavigationTiming | undefined;
  const navigationType = navigation?.type;
  const collector = new TelemetryCollector({
    now,
    id,
    session: read,
    touch: write,
    environment: {
      device: browser.matchMedia("(max-width: 767px)").matches
        ? "mobile"
        : "desktop",
      displayMode:
        browser.matchMedia("(display-mode: standalone)").matches ||
        ("standalone" in browser.navigator &&
          browser.navigator.standalone === true)
          ? "standalone"
          : "browser",
      navigationType:
        navigationType === "navigate" ||
        navigationType === "reload" ||
        navigationType === "back_forward"
          ? navigationType
          : "unknown",
      appVersion: env.VITE_APP_VERSION,
    },
    send: async (batch, scope, keepalive, signal) => {
      const snapshot = authSession.getSnapshot();
      if (!snapshot.session || !authSession.isCurrent(scope)) return "discard";
      return sendTelemetryBatch(
        batch,
        snapshot.session.accessToken,
        scope.tenantId,
        keepalive,
        signal,
      );
    },
  });
  const disconnect = installTelemetryCollector(collector);
  const synchronize = () => {
    const { session, requestScope } = authSession.getSnapshot();
    const membership = session?.user.tenants.find(
      (tenant) => tenant.tenantId === requestScope?.tenantId,
    );
    collector.setScope(
      requestScope?.tenantId && membership
        ? {
            ...requestScope,
            tenantId: requestScope.tenantId,
            roles: membership.roles,
          }
        : null,
    );
  };
  const unsubscribe = authSession.subscribe(synchronize);
  synchronize();
  collector.setVisible(browser.document.visibilityState !== "hidden");
  const activity = () => collector.activity();
  const visibility = () => {
    collector.setVisible(browser.document.visibilityState !== "hidden");
    if (browser.document.visibilityState === "hidden")
      void collector.flush(true);
  };
  for (const event of ["pointerdown", "keydown", "scroll", "touchstart"])
    browser.document.addEventListener(event, activity, {
      passive: true,
      capture: true,
    });
  browser.document.addEventListener("visibilitychange", visibility);
  const timer = browser.setInterval(() => {
    collector.tick();
    void collector.flush();
  }, 30_000);
  return () => {
    browser.clearInterval(timer);
    unsubscribe();
    disconnect();
    for (const event of ["pointerdown", "keydown", "scroll", "touchstart"])
      browser.document.removeEventListener(event, activity, true);
    browser.document.removeEventListener("visibilitychange", visibility);
  };
}
